import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Empty, ErrorState, Loading, cx } from "@/components/ui";
import { BoutiquePicker } from "@/components/BoutiquePicker";
import { CategoryPicker, SearchField, StatRow, StatTile, stockLevel } from "@/components/pc/Kit";
import { StockTable, type StockTableRow } from "@/components/pc/StockTable";
import { PageHeader, PageStack } from "@/layouts/DesktopShell";
import { useNetworkStock } from "@/lib/queries";
import { useSession } from "@/hooks/useSession";
import { useUi } from "@/hooks/useUi";
import { eur, num } from "@/lib/format";
import { categoryKey, groupCategories } from "@/lib/categories";

type ScopeMode = "toutes" | "seulement" | "sauf";

/**
 * Filtre de boutiques de la page Produits, gardé dans l'URL :
 * `?seulement=a,b` (ces boutiques uniquement) ou `?sauf=a,b` (toutes sauf).
 */
function useBoutiqueScope() {
  const { me } = useSession();
  const [params, setParams] = useSearchParams();
  const all = me.boutiques.map((b) => b.id);
  const only = params.get("seulement")?.split(",").filter((id) => all.includes(id)) ?? null;
  const except = params.get("sauf")?.split(",").filter((id) => all.includes(id)) ?? null;
  const mode: ScopeMode = only ? "seulement" : except ? "sauf" : "toutes";
  const picked = only ?? except ?? [];
  const ids = mode === "seulement" ? picked : mode === "sauf" ? all.filter((id) => !picked.includes(id)) : all;

  const set = (m: ScopeMode, list: string[]) => {
    const next = new URLSearchParams(params);
    next.delete("seulement");
    next.delete("sauf");
    // En mode « seulement » / « sauf », la liste (même vide) est gardée pour
    // que le sélecteur reste ouvert pendant le choix.
    if (m !== "toutes") next.set(m, list.join(","));
    setParams(next, { replace: true });
  };
  return { mode, picked, ids, set, total: all.length };
}

/** Sélecteur « Toutes / Seulement / Toutes sauf » + boutiques, remis à zéro d'un clic. */
function BoutiqueScopeFilter({ scope }: { scope: ReturnType<typeof useBoutiqueScope> }) {
  const { me } = useSession();
  const { mode, picked, set } = scope;
  const modes: { key: ScopeMode; label: string }[] = [
    { key: "toutes", label: "Toutes les boutiques" },
    { key: "seulement", label: "Seulement…" },
    { key: "sauf", label: "Toutes sauf…" },
  ];
  const active = mode !== "toutes" && picked.length > 0;
  return (
    <div className={cx("flex flex-wrap items-center gap-[10px]", active && "rounded-ios bg-accent-soft px-4 py-2")}>
      <span className="text-[13px] text-muted">Boutiques</span>
      <div className="flex gap-1 rounded-[9px] bg-ios-fill p-[2px]">
        {modes.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => set(m.key, m.key === "toutes" ? [] : picked)}
            className={cx(
              "rounded-[7px] px-3 py-[5px] text-[13px]",
              mode === m.key ? "bg-white font-semibold text-ink shadow-[0_3px_8px_rgba(0,0,0,0.12)]" : "font-medium text-ink"
            )}
          >
            {m.label}
          </button>
        ))}
      </div>
      {mode !== "toutes" && (
        <div className="min-w-[240px] flex-1">
          <BoutiquePicker
            boutiques={me.boutiques}
            value={picked}
            multiple
            placeholder={mode === "seulement" ? "Choisir les boutiques à afficher…" : "Choisir les boutiques à exclure…"}
            onChange={(list) => set(mode, list)}
          />
        </div>
      )}
      {active && (
        <button
          type="button"
          onClick={() => set("toutes", [])}
          className="rounded-full bg-white px-3 py-[6px] text-[12px] font-bold text-accent ring-1 ring-line hover:text-danger"
        >
          × Retirer le filtre
        </button>
      )}
    </div>
  );
}

/** « Produits » : catalogue consolidé du périmètre (déjà filtré côté serveur pour un gérant). */
export function StocksPage() {
  const { me } = useSession();
  const { openProduct } = useUi();
  const scope = useBoutiqueScope();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  // Clé de catégorie regroupée (voir lib/categories) ; "" = toutes.
  const [category, setCategory] = useState("");
  const filtered = scope.mode !== "toutes" && scope.picked.length > 0;
  // Sans filtre, on ne passe rien : l'API prend tout le périmètre.
  const q = useNetworkStock(filtered ? scope.ids : undefined, scope.ids.length > 0);
  const ruptureOnly = params.get("filtre") === "rupture";

  const rows: StockTableRow[] = useMemo(
    () =>
      (q.data?.rows ?? []).map((r) => ({
        key: r.key,
        id: r.id,
        name: r.name,
        category: r.category,
        stock: r.stock,
        sold30: r.sold30,
        coverageDays: r.coverageDays,
        price: r.price,
        supplyPrice: r.supplyPrice,
        promo: r.promo,
        boutiquesRupture: r.boutiquesRupture,
        level: r.boutiquesRupture > 0 ? "rupture" : stockLevel(r.stock, r.coverageDays),
      })),
    [q.data]
  );
  const categories = useMemo(() => groupCategories(rows.map((r) => r.category)), [rows]);
  const needle = normalize(search.trim());
  const shown = rows.filter(
    (r) =>
      (!category || (r.category != null && categoryKey(r.category) === category)) &&
      (!ruptureOnly || (r.boutiquesRupture ?? 0) > 0) &&
      (!needle || normalize(`${r.name} ${r.category ?? ""}`).includes(needle))
  );

  return (
    <PageStack>
      <PageHeader
        title="Produits"
        subtitle={`${q.data ? `${num(rows.length)} références · ` : ""}stock et ventes ${me.networkWide ? "de tout le réseau" : "de mes boutiques"}`}
      />
      {me.boutiques.length > 1 && <BoutiqueScopeFilter scope={scope} />}
      {scope.ids.length === 0 ? (
        <Empty>Toutes les boutiques sont exclues : retirez le filtre pour voir les produits.</Empty>
      ) : q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorState error={q.error} />
      ) : (
        q.data && (
          <>
            <StatRow>
              <StatTile label="Références" value={num(rows.length)} />
              <StatTile label="Valeur du stock (achat)" value={eur(q.data.kpis.costValue)} />
              <StatTile label="Produits en rupture quelque part" value={num(rows.filter((r) => (r.boutiquesRupture ?? 0) > 0).length)} tone="danger" />
              <StatTile label={me.networkWide ? "Ventes 30 j (réseau)" : "Ventes 30 j (mes boutiques)"} value={`${num(rows.reduce((s, r) => s + r.sold30, 0))} u.`} />
            </StatRow>
            <div className="flex flex-wrap items-center gap-3">
              <SearchField value={search} onChange={setSearch} className="w-[400px] max-w-full" />
              {categories.length > 1 && <CategoryPicker options={categories} value={category} onChange={setCategory} total={rows.length} />}
              {ruptureOnly && (
                <button
                  type="button"
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    next.delete("filtre");
                    setParams(next, { replace: true });
                  }}
                  className="min-h-[32px] rounded-full bg-danger-bg px-[13px] text-[14px] font-medium text-danger"
                >
                  En rupture ×
                </button>
              )}
            </div>
            <StockTable
              rows={shown}
              mode="catalogue"
              stockLabel={me.networkWide ? "Stock réseau" : "Stock total"}
              onOpen={(r) => openProduct({ id: r.id, name: r.name, scope: "reseau" })}
            />
            {filtered && (
              <div className="px-1 text-[12px] text-muted">
                Calculé sur {num(scope.ids.length)} boutique{scope.ids.length > 1 ? "s" : ""} sur {num(scope.total)}
              </div>
            )}
          </>
        )
      )}
    </PageStack>
  );
}

function normalize(s: string) {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}
